import { useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import {
  format,
  isAfter,
  isBefore,
  isEqual,
  isValid,
  parse,
  startOfDay,
} from 'date-fns';
import { ExternalLinkIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useSnapshot } from 'valtio';

import { DialogDeleteRecordConfirmation } from '../../../components/pages/backoffice/dialogs/delete-record.jsx';
import { DialogInvoiceAudit } from '../../../components/pages/backoffice/dialogs/invoice-audit.jsx';
import { BackOfficePermissionsRedirectWrapper } from '../../../components/pages/backoffice/permissions-redirect-wrapper.jsx';
import DecorativeInput from '../../../components/decorative-input.jsx';
import Error from '../../../components/feedback/error.jsx';
import Loading from '../../../components/feedback/loading.jsx';
import ImageInspector from '../../../components/image-inspector.jsx';
import Heading from '../../../components/typography/heading.jsx';
import { Button } from '../../../components/ui/button.jsx';
import { Combobox } from '../../../components/ui/combobox.jsx';
import { DateInputWithDatePicker } from '../../../components/ui/date-picker.jsx';
import FormCard from '../../../components/ui/form-card.jsx';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../components/ui/form.jsx';
import { InputCurrency } from '../../../components/ui/input-currency.jsx';
import { Input } from '../../../components/ui/input.jsx';
import { msCatalystApi } from '../../../services/api/ms-catalyst-api.js';
import { authState } from '../../../services/state/auth-state.jsx';
import {
  DATE_FORMAT,
  DATETIME_FORMAT_STANDARD,
} from '../../../utils/constants.js';
import { formatDateToISO } from '../../../utils/format.js';
import { REGEX_DATE_EU } from '../../../utils/regex.js';

export function InvoicesEdit() {
  const { user, isAdmin } = useSnapshot(authState);
  const navigate = useNavigate();
  const { invoiceId } = useParams();

  const [proofFileType, setProofFileType] = useState('');

  const formUpdateInvoice = useForm({
    mode: 'onChange',
    defaultValues: {
      code: '',
      amount: '',
      shopId: '',
      issuedDate: '',
      campaign: undefined,
    },
  });

  const queryInvoice = useQuery({
    queryKey: ['invoice_update'],
    queryFn: async () => {
      const { data: invoice } = await msCatalystApi.get(
        `/invoices/${invoiceId}`,
      );
      const invoiceData = invoice.data;

      const rawIssuedDate = invoiceData?.invoices?.issuedDate;

      const isValidDate = rawIssuedDate && isValid(new Date(rawIssuedDate));

      const formattedIssuedDate = isValidDate
        ? format(new Date(rawIssuedDate), 'dd-MM-yyyy')
        : format(new Date(), 'dd-MM-yyyy');

      formUpdateInvoice.reset({
        code: invoiceData?.code || '',
        amount: invoiceData.amount,
        shopId: {
          label: invoiceData?.shops?.name || undefined,
          value: invoiceData?.shops?.ROWID || undefined,
        },
        issuedDate: formattedIssuedDate,
        campaign: {
          minEntryAmount: invoiceData?.campaigns?.minEntryAmount,
        },
      });

      if (invoiceData.invoices?.proofFilename) {
        const splitFilename = invoiceData.invoices.proofFilename.split('.');
        const fileExtension = splitFilename[splitFilename.length - 1];

        setProofFileType(fileExtension);
      }

      const proofFileUrlLastSlashIndex =
        invoiceData.invoices.proofFileUrl.lastIndexOf('/');
      const proofFileUrlBaseUrl = invoiceData.invoices.proofFileUrl.substring(
        0,
        proofFileUrlLastSlashIndex + 1,
      );
      const proofFileFilename = invoiceData.invoices.proofFileUrl.substring(
        proofFileUrlLastSlashIndex + 1,
      );

      return {
        customer: `${invoiceData.customers.firstName} ${invoiceData.customers.lastName}`,
        campaign: invoiceData.campaigns.name,
        campaignId: invoiceData.campaigns.ROWID,
        campaignStartDate: invoiceData.campaigns.startDate,
        campaignCloseDate: invoiceData.campaigns.closeDate,
        amount: invoiceData.invoices.amount,
        issuedDate: invoiceData.invoices.issuedDate,
        code: invoiceData.invoices.code,
        status: invoiceData.invoices.status,
        shopId: invoiceData.shops.ROWID,
        shop: invoiceData.shops.name,
        proofFileUrl:
          proofFileUrlBaseUrl + encodeURIComponent(proofFileFilename),
      };
    },
  });

  const queryCampaignAdherentShops = useQuery({
    queryKey: ['invoice_update_campaign_adherent_shops'],
    queryFn: async () => {
      const { data: campaignAdherentShops } = await msCatalystApi.get(
        `/campaigns/adherent-shops?campaignId=${queryInvoice.data.campaignId}`,
      );

      return campaignAdherentShops.data;
    },
    select: (data) => {
      return data.map((item) => ({
        label: item.shops.name,
        value: item.shops.ROWID,
      }));
    },
    enabled: !!queryInvoice.data,
  });

  const queryInvoicePdf = useQuery({
    queryKey: ['invoice_update_pdf'],
    queryFn: async () => {
      const response = await fetch(queryInvoice?.data?.proofFileUrl);
      const blob = await response.blob();

      if (blob) {
        return URL.createObjectURL(blob);
      } else {
        return null;
      }
    },
    enabled:
      !!queryInvoice.data &&
      queryInvoice?.data?.proofFileUrl &&
      proofFileType === 'pdf',
  });

  const mutationDeleteInvoice = useMutation({
    mutationFn: async () => {
      await msCatalystApi.delete(`/invoices/${invoiceId}`);
    },
  });

  const mutationUpdateInvoice = useMutation({
    mutationFn: async ({ data, status }) => {
      data.status = status;
      delete data.campaign;

      await msCatalystApi.put(`/invoices/${invoiceId}`, {
        ...data,
        issuedDate: formatDateToISO(data.issuedDate),
        shopId: data.shopId.value,
        staffId: user.staffId,
      });
    },
    onSuccess: (data, variables) => {
      if (variables.status === 'Aprovada')
        toast.success('Fatura aprovada com sucesso!');

      if (variables.status === 'Rejeitada')
        toast.success('Fatura rejeitada com sucesso!');

      navigate(`/backoffice/invoices/${invoiceId}`);
    },
    onError: (err) => {
      const message = err?.response?.data?.message;

      if (
        message &&
        message?.toLowerCase().includes('duplicate value for uniquecode')
      ) {
        toast.error('Fatura duplicada - Esta fatura já existe no sistema');
      } else {
        toast.error('Erro ao atualizar fatura');
      }
    },
  });

  const onFormSubmit = async (data, status) => {
    try {
      await mutationUpdateInvoice.mutateAsync({ data, status });

      if (status !== 'Rejeitada') {
        await msCatalystApi.get(`/invoices/generate-coupons/${invoiceId}`);
      }
    } catch (err) {
      toast.error('Erro ao inserir fatura, não serão gerados quaisquer cupões');
    }
  };

  if (queryInvoice.isLoading || queryInvoice.isRefetching) {
    return <Loading page={true} />;
  }

  if (queryInvoice.isError || queryInvoice.isRefetchError) {
    return <Error />;
  }

  console.log(
    'formUpdateInvoice.formState.isValid',
    formUpdateInvoice.formState.isValid,
  );

  return (
    <BackOfficePermissionsRedirectWrapper>
      <div className="flex flex-col items-start justify-start gap-y-6 pb-6 pt-4 lg:flex-row lg:items-center lg:justify-between lg:gap-y-0">
        <div>
          <Heading className="pb-2">Fatura</Heading>

          <h2 className="text-lg text-zinc-600 dark:text-zinc-400">{`${queryInvoice.data?.campaign} - ${queryInvoice.data?.customer}`}</h2>
        </div>

        <div className="flex flex-row flex-wrap justify-end gap-4">
          <Button asChild>
            <a
              href={queryInvoice?.data?.proofFileUrl}
              target="_blank"
              rel="noreferrer noopener"
            >
              <ExternalLinkIcon />
              Comprovativo
            </a>
          </Button>

          {isAdmin ? (
            <DialogDeleteRecordConfirmation
              recordName={queryInvoice?.data?.code}
              redirect="/backoffice/invoices"
              disabled={formUpdateInvoice.formState.isSubmitting}
              action={async () => mutationDeleteInvoice.mutateAsync()}
            />
          ) : null}

          {queryInvoice?.data?.status === 'Pendente' && (
            <>
              <DialogInvoiceAudit
                disabled={formUpdateInvoice.formState.isSubmitting}
                action={async (reason) => {
                  const data = formUpdateInvoice.getValues();
                  await onFormSubmit({ ...data, reason }, 'Rejeitada');
                }}
                type="reject"
              />
              <DialogInvoiceAudit
                disabled={
                  !formUpdateInvoice.formState.isValid ||
                  formUpdateInvoice.formState.isSubmitting
                }
                action={async (reason) => {
                  await formUpdateInvoice.handleSubmit((data) =>
                    onFormSubmit({ ...data, reason }, 'Aprovada'),
                  )();
                }}
                type="approve"
              />
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 items-start gap-8 xl:grid-cols-3">
        <div className="static col-span-1 gap-5 pt-2 xl:sticky xl:top-0 xl:col-span-1">
          <Form {...formUpdateInvoice}>
            <form>
              <FormCard className="lg:grid-cols-1">
                <DecorativeInput
                  value={queryInvoice.data?.customer}
                  label="Cliente"
                  className="opacity-40"
                />

                <FormField
                  control={formUpdateInvoice.control}
                  name="code"
                  rules={{
                    required: 'N.º da fatura é um campo obrigatório',
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>N.º da fatura</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formUpdateInvoice.control}
                  name="amount"
                  rules={{
                    required: 'Montante da Fatura é um campo obrigatório',
                    validate: {
                      isNumber: (value) => {
                        const numValue = Number(value);
                        return (
                          !isNaN(numValue) ||
                          'Montante da Fatura tem de ser um número'
                        );
                      },
                      minAmount: (value, formValues) => {
                        if (formValues.campaign?.minEntryAmount) {
                          const numValue = Number(value);
                          return (
                            numValue >= formValues.campaign.minEntryAmount ||
                            'Montante da Fatura é inferior ao valor de participação da Campanha'
                          );
                        }
                        return true;
                      },
                    },
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>Valor da Fatura (€)</FormLabel>
                      <FormControl>
                        <InputCurrency {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={formUpdateInvoice.control}
                  name="shopId"
                  rules={{
                    required: 'Loja é um campo obrigatório',
                    validate: {
                      hasValue: (value) =>
                        (value?.value !== undefined &&
                          value?.label !== undefined) ||
                        'Loja é um campo obrigatório',
                    },
                  }}
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <FormLabel required={true}>Loja</FormLabel>
                        <FormControl>
                          <Combobox
                            isLoading={
                              queryCampaignAdherentShops.isLoading ||
                              queryCampaignAdherentShops.isPending ||
                              queryCampaignAdherentShops.isPaused
                            }
                            isError={queryCampaignAdherentShops.isError}
                            items={queryCampaignAdherentShops.data}
                            value={field.value?.label}
                            valueKey="value"
                            labelKey="label"
                            onChange={(value) => field.onChange(value)}
                          />
                        </FormControl>

                        <FormDescription>
                          A loja onde a fatura foi emitida
                        </FormDescription>

                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                <FormField
                  control={formUpdateInvoice.control}
                  name="issuedDate"
                  rules={{
                    required: 'Data de Emissão é um campo obrigatório',
                    validate: {
                      matchesFormat: (value) =>
                        REGEX_DATE_EU.test(value) ||
                        'A Data tem de seguir o formato: dd-MM-yyyy',
                      validDate: (value) => {
                        try {
                          const parsedDate = parse(
                            value,
                            'dd-MM-yyyy',
                            new Date(),
                          );
                          return (
                            isValid(parsedDate) || 'Insira uma data válida'
                          );
                        } catch (error) {
                          return 'Insira uma data válida';
                        }
                      },
                      dateWithinRange: (value) => {
                        const parsedIssuedDate = startOfDay(
                          parse(value, DATE_FORMAT, new Date()),
                        );
                        const parsedStartDate = startOfDay(
                          parse(
                            queryInvoice.data?.campaignStartDate,
                            DATETIME_FORMAT_STANDARD,
                            new Date(),
                          ),
                        );
                        const parsedCloseDate = startOfDay(
                          parse(
                            queryInvoice.data?.campaignCloseDate,
                            DATETIME_FORMAT_STANDARD,
                            new Date(),
                          ),
                        );

                        if (
                          isValid(parsedIssuedDate) &&
                          isValid(parsedStartDate) &&
                          isValid(parsedCloseDate)
                        ) {
                          const isWithinRange =
                            (isAfter(parsedIssuedDate, parsedStartDate) ||
                              isEqual(parsedIssuedDate, parsedStartDate)) &&
                            (isBefore(parsedIssuedDate, parsedCloseDate) ||
                              isEqual(parsedIssuedDate, parsedCloseDate));

                          console.log('isWithinRange', isWithinRange);

                          return (
                            isWithinRange ||
                            'Data de Emissão deve estar entre a data de início e a data de fecho da campanha'
                          );
                        }

                        return false;
                      },
                    },
                  }}
                  render={({ field }) => {
                    let selectedDate = null;

                    if (field?.value?.match(REGEX_DATE_EU)) {
                      const [day, month, year] = field.value.split('-');
                      selectedDate = new Date(`${year}-${month}-${day}`);
                    }

                    return (
                      <FormItem>
                        <FormLabel required={true}>
                          Data de Emissão da Fatura
                        </FormLabel>
                        <FormControl>
                          <DateInputWithDatePicker
                            input={{
                              ...field,
                              onChange: (e) => {
                                const date = e.target.value;

                                if (!date && !isValid(new Date(date))) return;

                                field.onChange(date);
                              },
                            }}
                            calendar={{
                              selected: selectedDate,
                              onSelect: (date) => {
                                if (!date && !isValid(new Date(date))) return;

                                field.onChange(format(date, 'dd-MM-yyyy'));
                              },
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </FormCard>
            </form>
          </Form>
        </div>

        <div className="col-span-1 pt-2 xl:col-span-2">
          {!queryInvoice.data.proofFileUrl ? (
            <h2>Nenhum comprovativo da fatura foi submetido</h2>
          ) : proofFileType === 'pdf' ? (
            <>
              {queryInvoicePdf.isEnabled && (
                <>
                  {queryInvoicePdf.isLoading ? (
                    <Loading message="A carregar PDF" />
                  ) : queryInvoicePdf.isError ? (
                    <Error errorMessage="Erro ao carregar PDF" />
                  ) : (
                    <iframe
                      src={
                        queryInvoicePdf?.data ||
                        'https://pdfobject.com/pdf/sample.pdf'
                      }
                      className="h-[900px]"
                      width="100%"
                      title="PDF Viewer"
                    />
                  )}
                </>
              )}
            </>
          ) : (
            <ImageInspector src={queryInvoice.data.proofFileUrl} />
          )}
        </div>
      </div>
    </BackOfficePermissionsRedirectWrapper>
  );
}
